import { Injectable } from '@angular/core';
import { JsonConvert, ValueCheckingMode } from 'json2typescript';
import { NgxSpinnerService } from 'ngx-spinner';
import { ErrorService } from '../error/error.service';

@Injectable({
  providedIn: 'root'
})
export class ApiBaseService {
  protected _jsonConvert = new JsonConvert();

  constructor(
    protected spinner: NgxSpinnerService,
    protected errorService: ErrorService
  ) {
    this._jsonConvert.valueCheckingMode = ValueCheckingMode.ALLOW_NULL;
  }

  protected serialize<T extends object>(obj: T) {
    try {
      return this._jsonConvert.serialize(obj);
    } catch (e) {
      this.spinner.hide();
      this.errorService.errors = [
        'Serializing Error: ' + obj.constructor.name + '. Check stacktrace for more details.'
      ];
      throw e;
    }
  }

  protected deserializeAs = <T extends object>(Model: new () => T) => (response: any): T | null => {
    return response ? this._jsonConvert.deserializeObject(response, Model) : null;
  };

  protected deserializeArrayAs = <T extends object>(Model: new () => T) => (response: any): T[] => {
    return response ? this._jsonConvert.deserializeArray(response, Model) : [];
  };

  protected deserializeTupleAs =
    <A extends object, B extends object>(ModelA: new () => A, ModelB: new () => B) =>
    (response: any): [A[], B[]] => {
      return [
        response[0].map(this.deserializeAs(ModelA)),
        response[1].map(this.deserializeAs(ModelB))
      ];
    };

  protected deserializeArraySortedAs =
    <T extends object>(
      Model: { new (): T; sorter: (a: T, b: T) => number; deserialize?: (x: any) => T }
    ) =>
    (response: any): T[] => {
      if (Model.deserialize) {
        return response.map(Model.deserialize).sort(Model.sorter);
      } else {
        return this.deserializeArrayAs(Model)(response).sort(Model.sorter);
      }
    };

  protected serializePrimitive(
    key: string,
    obj: any,
    output: any,
    prefix: string | null = null,
    suffix: string | number | null = null // ✅ FIXED HERE
  ) {
    if (typeof obj === 'string' || typeof obj === 'number' || typeof obj === 'boolean') {
      if (prefix != null) key = `${prefix}[${key}]`;
      if (suffix != null) key = `${key}[${suffix}]`;
      output = output.set(key, obj);
    }
    return output;
  }

  protected serializeParams(obj: Record<string, any>, output: any, prefix: string | null = null) {
    if (!obj) return output;

    for (const key in obj) {
      if (!Object.prototype.hasOwnProperty.call(obj, key)) continue;

      const val = obj[key];
      const type = typeof val;

      if (Array.isArray(val)) {
        val.forEach((f, i) => {
          output = this.serializePrimitive(key, f, output, prefix, i); // ✅ Works now
        });
      } else if (type === 'object' && val !== null) {
        // Handle nested objects recursively
        output = this.serializeParams(val, output, key);
      } else {
        output = this.serializePrimitive(key, val, output, prefix);
      }
    }

    return output;
  }

  protected serializeParamsObj(obj: any, output: any) {
    output = this.serialize(obj);
    return output;
  }
}
